import { Check, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { isStepUnlocked, stepStatus } from "@/lib/wizardSteps";

interface WizardStepperProps {
  /** Readonly: the canonical list is a shared `as const` tuple. */
  steps: readonly string[];
  currentStep: number;
  /** The furthest step the producer may navigate to. */
  maxUnlockedStep: number;
  onStepClick: (step: number) => void;
}

/**
 * Left-hand vertical step navigation for the production wizard. Lets the
 * producer jump straight between steps they've already completed instead
 * of clicking Previous repeatedly. Steps whose required info isn't filled
 * yet are locked so nothing important is skipped — see wizardSteps.ts.
 */
export function WizardStepper({
  steps,
  currentStep,
  maxUnlockedStep,
  onStepClick,
}: WizardStepperProps) {
  return (
    <nav aria-label="Production steps" className="space-y-1">
      {steps.map((label, index) => {
        const status = stepStatus(index, currentStep, maxUnlockedStep);
        const unlocked = isStepUnlocked(index, maxUnlockedStep);
        return (
          <button
            key={index}
            type="button"
            disabled={!unlocked}
            aria-current={status === "current" ? "step" : undefined}
            onClick={() => unlocked && onStepClick(index)}
            title={unlocked ? undefined : "Fill in the previous steps first"}
            className={cn(
              "flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-[15px] transition-colors",
              status === "current" && "bg-primary/10 font-semibold text-primary",
              (status === "done" || status === "available") &&
                "font-medium text-foreground hover:bg-muted",
              status === "locked" && "cursor-not-allowed text-muted-foreground/50",
            )}
          >
            <span
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold",
                status === "current" && "border-primary bg-primary text-primary-foreground",
                status === "done" && "border-primary bg-primary/20 text-primary",
                status === "available" && "border-border",
                status === "locked" && "border-border",
              )}
            >
              {status === "done" ? (
                <Check className="h-4 w-4" />
              ) : status === "locked" ? (
                <Lock className="h-3.5 w-3.5" />
              ) : (
                index + 1
              )}
            </span>
            <span className="truncate">{label}</span>
          </button>
        );
      })}
    </nav>
  );
}
